import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/auth';
import { Observable, of, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

@Injectable({
  providedIn: 'root',
})
export class CloudFunctionsApiService {
  private baseUrl = environment.cloudFunctionsApiUrl;

  constructor(private http: HttpClient, private afAuth: AngularFireAuth) {}

  get<T = any>(path: string, fallback?: T): Observable<T> {
    return this.request<T>('GET', path).pipe(
      catchError(() => of(fallback as T))
    );
  }

  post<T = any>(path: string, body?: any): Observable<T> {
    return this.request<T>('POST', path, body);
  }

  patch<T = any>(path: string, body?: any): Observable<T> {
    return this.request<T>('PATCH', path, body);
  }

  put<T = any>(path: string, body?: any): Observable<T> {
    return this.request<T>('PUT', path, body);
  }

  delete<T = any>(path: string): Observable<T> {
    return this.request<T>('DELETE', path);
  }

  snapshotList(path: string): Observable<any[]> {
    return this.get<any[]>(path, []).pipe(
      map((items) => this.toSnapshotChanges(this.ensureArray(items)))
    );
  }

  valueList(path: string): Observable<any[]> {
    return this.get<any[]>(path, []).pipe(map((items) => this.ensureArray(items)));
  }

  getCollection(path: string): Observable<any> {
    return this.get<any[]>(path, []).pipe(
      map((items) => this.toQuerySnapshot(this.ensureArray(items)))
    );
  }

  getDocument(path: string): Observable<any> {
    return this.get<any>(path, null).pipe(map((item) => this.toDocumentSnapshot(item)));
  }

  documentChange(path: string): Observable<any> {
    return this.get<any>(path, null).pipe(
      map((item) => ({
        payload: {
          id: this.getId(item),
          exists: !!item,
          data: () => item,
        },
      }))
    );
  }

  private request<T>(method: HttpMethod, path: string, body?: any): Observable<T> {
    return from(this.afAuth.currentUser).pipe(
      switchMap((user) => {
        if (user) {
          return from(user.getIdToken());
        }

        return of(null);
      }),
      switchMap((idToken) => {
        const headers = idToken
          ? new HttpHeaders({ Authorization: `Bearer ${idToken}` })
          : new HttpHeaders();
        const url = `${this.baseUrl}${path}`;
        return this.http.request<T>(method, url, { body, headers }).pipe(
          map((response) => this.toClientValue(response) as T)
        );
      })
    );
  }

  private toClientValue(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.toClientValue(item));
    }

    // Firestore Timestamps serialize as { seconds: N, nanoseconds: N }
    if (value && typeof value === 'object' && typeof value.seconds === 'number' && typeof value.nanoseconds === 'number') {
      const date: any = new Date(value.seconds * 1000 + value.nanoseconds / 1000000);
      date.toDate = () => date;
      return date;
    }

    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        value[key] = this.toClientValue(value[key]);
      });
      this.addEnglishAliases(value);
      return value;
    }

    if (typeof value === 'string' && this.isIsoDate(value)) {
      const date: any = new Date(value);
      date.toDate = () => date;
      return date;
    }

    return value;
  }

  private isIsoDate(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) && !isNaN(Date.parse(value));
  }

  private ensureArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value) {
      return [];
    }
    return [value];
  }

  private getId(item: any): string {
    return item?.id || item?._id || item?.uid || item?.doc_id || item?.docId || item?.documentId || item?.document_id || item?.eventId || item?.event_id || item?.idEvento || item?.eventoId || item?.idevento || item?.code || item?.class_code || '';
  }

  private addEnglishAliases(item: any): void {
    this.copyIfMissing(item, 'nombre', 'name');
    this.copyIfMissing(item, 'display_name', 'name');
    this.copyIfMissing(item, 'nombres', 'firstName');
    this.copyIfMissing(item, 'apellidos', 'lastName');
    this.copyIfMissing(item, 'apellido', 'lastName');
    this.copyIfMissing(item, 'usuario', 'username');
    this.copyIfMissing(item, 'colegios', 'schools');
    this.copyIfMissing(item, 'clases', 'classes');
    this.copyIfMissing(item, 'cursos', 'courses');
    this.copyIfMissing(item, 'fechaAlerta', 'alertDate');
    this.copyIfMissing(item, 'alert_date', 'alertDate');
    this.copyIfMissing(item, 'alert_date', 'fechaAlerta');
    this.copyIfMissing(item, 'idAlerta', 'alertId');
    this.copyIfMissing(item, 'alert_id', 'alertId');
    this.copyIfMissing(item, 'alert_id', 'idAlerta');
    this.copyIfMissing(item, 'idChat', 'chatId');
    this.copyIfMissing(item, 'idColegio', 'schoolId');
    this.copyIfMissing(item, 'school_id', 'schoolId');
    this.copyIfMissing(item, 'school_id', 'idColegio');
    this.copyIfMissing(item, 'idSupervisor', 'supervisorId');
    this.copyIfMissing(item, 'supervisor_id', 'supervisorId');
    this.copyIfMissing(item, 'supervisor_id', 'idSupervisor');
    this.copyIfMissing(item, 'idUsuario', 'userId');
    this.copyIfMissing(item, 'user_id', 'userId');
    this.copyIfMissing(item, 'user_id', 'idUsuario');
    this.copyIfMissing(item, 'mensaje', 'message');
    this.copyIfMissing(item, 'message', 'mensaje');
    this.copyIfMissing(item, 'mostrarSupervisor', 'showSupervisor');
    this.copyIfMissing(item, 'supervisor_visible', 'showSupervisor');
    this.copyIfMissing(item, 'supervisor_visible', 'mostrarSupervisor');
    this.copyIfMissing(item, 'director_visible', 'mostrarDirector');
    this.copyIfMissing(item, 'avatarColegio', 'schoolAvatar');
    this.copyIfMissing(item, 'school_avatar', 'schoolAvatar');
    this.copyIfMissing(item, 'school_avatar', 'avatarColegio');
    this.copyIfMissing(item, 'avatarAlumno', 'studentAvatar');
    this.copyIfMissing(item, 'student_avatar', 'studentAvatar');
    this.copyIfMissing(item, 'student_avatar', 'avatarAlumno');
    this.copyIfMissing(item, 'reportada', 'reported');
    this.copyIfMissing(item, 'reported', 'reportada');
    this.copyIfMissing(item, 'student_name', 'name');
    this.copyIfMissing(item, 'student_name', 'nombre');
    this.copyIfMissing(item, 'director_id', 'idDirector');
    this.copyIfMissing(item, 'resumen', 'summary');
    this.copyIfMissing(item, 'severidad', 'severity');
  }

  private copyIfMissing(item: any, legacyKey: string, englishKey: string): void {
    if (item?.[englishKey] === undefined && item?.[legacyKey] !== undefined) {
      item[englishKey] = item[legacyKey];
    }
  }

  private toSnapshotChanges(items: any[]): any[] {
    return items.map((item) => {
      const id = this.getId(item);

      if (item && typeof item === 'object' && id && item.id === undefined) {
        item.id = id;
      }

      return {
        payload: {
          doc: {
            id,
            data: () => item,
          },
        },
      };
    });
  }

  private toQuerySnapshot(items: any[]): any {
    const docs = items.map((item) => ({
      id: this.getId(item),
      exists: true,
      data: () => item,
    }));
    return {
      empty: docs.length === 0,
      size: docs.length,
      docs,
      forEach: (callback: any) => docs.forEach(callback),
    };
  }

  private toDocumentSnapshot(item: any): any {
    return {
      id: this.getId(item),
      exists: !!item,
      data: () => item,
    };
  }
}
